/**
 * Live client receipt for one automatic ability-capture sequence.
 *
 * The Ability Grounds lab consumes this instead of guessing with a fixed
 * sleep. A capture is complete only after the scheduled burst, the 4s
 * lifecycle-beat window, and every queued upload have settled.
 */
export type AbilityCaptureState = 'started' | 'complete' | 'dropped';

export interface AbilityCaptureStatus {
  castId: string;
  abilityId: string;
  targetId: string | null;
  state: AbilityCaptureState;
  startedAt: number;
  completedAt?: number;
  runtimeMs?: number;
  scheduledOffsets: number[];
  expectedFrames: number;
  uploadedFrames: number;
  failedFrames: number;
  eventFrames: number;
  dropReason?: 'throttle' | 'concurrency';
}

type Listener = (status: AbilityCaptureStatus) => void;

let latestStatus: AbilityCaptureStatus | null = null;
const listeners = new Set<Listener>();

export function publishAbilityCaptureStatus(status: AbilityCaptureStatus): void {
  latestStatus = status;
  for (const listener of listeners) listener(status);
}

export function latestAbilityCaptureStatus(): AbilityCaptureStatus | null {
  return latestStatus;
}

export function subscribeAbilityCaptureStatus(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function waitForAbilityCapture(
  abilityId: string,
  targetId: string | null,
  startedAfter: number,
  timeoutMs = 9_000,
): Promise<AbilityCaptureStatus> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeout = 0;
    let unsubscribe: () => void = () => {};
    const finish = (status: AbilityCaptureStatus) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      unsubscribe();
      if (status.state === 'complete') resolve(status);
      else reject(new Error(`capture ${status.dropReason ?? 'dropped'}`));
    };
    const matches = (status: AbilityCaptureStatus) => {
      if (status.abilityId !== abilityId) return;
      if ((status.targetId ?? null) !== (targetId ?? null)) return;
      if (status.startedAt < startedAfter) return;
      if (status.state === 'complete' || status.state === 'dropped') finish(status);
    };
    unsubscribe = subscribeAbilityCaptureStatus(matches);
    if (latestStatus) matches(latestStatus);
    if (settled) return;
    timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      reject(new Error(`capture timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });
}

/** Test seam. */
export function __resetAbilityCaptureStatusForTests(): void {
  latestStatus = null;
  listeners.clear();
}
